//#region Angular Imports
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';

import { CommonModule } from '@angular/common';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DragDropModule } from '@angular/cdk/drag-drop';

//#endregion

import { AtlasSharedModule } from './shared/module/atlas-shared.module';
import { ConfirmationService } from 'primeng/api';

import { MessageService } from 'primeng/api';

//#endregion

//#region Atlas Services Imports
import { EnvironmentService } from './shared/environment/environment.service';
import { UtilService } from './shared/util/util.service';
import { LogService } from './shared/log/log.service';
import { LocalStorageService } from './shared/storage/local-storage.service';
import { PlatformService } from './shared/platform/platform.service';
import { ExceptionService } from './shared/exception/exception.service';
import { TranslationHandlerService } from './shared/localization/translation-handler.service';
import { AuthService } from './shared/auth/auth.service';
import { AuthGuard } from './shared/auth/auth-guard.service';
import { WlCanDeactivateRouteGuard } from './shared/route/can-deactivate-route-guard';
import { ApiService } from './shared/api/api.service';
import { ServiceApiService } from './shared/api/service-api.service';
import { HttpRequestService } from './shared/http/http-request.service';
import { NotificationService } from './shared/notification/notification.service';
import { DialogService } from './shared/dialog/dialog.service';
import { BreadcrumbService } from './shared/container/workspace/breadcrumb/breadcrumb.service';
import { DatagridService } from './shared/widgets/datagrid/datagrid.service';
import { TranslationService } from './shared/translation/translation.service';
import { NavigationService } from './shared/navigation/navigation.service';
import { DeviceUtilService } from './shared/util/deviceUtil.service';
import { ProfileService } from './profiles/shared/profile.service';
import { PortalAuthService } from './shared/portal-auth/portal-auth.service';
import { ProfileStatusService } from './profiles/profile-status/profile-status.service';
import { RelationTypeService } from './profiles/shared/relation-type.service';
import { CeuService } from './config/ceu/ceu.service';
import { WlEventsToolbarBroadcastService } from './shared/container/toolbars/module/events/broadcast/toolbar-events-broadcast.service';
import { WlProfilesToolbarBroadcastService } from './shared/container/toolbars/module/profiles/broadcast/toolbar-profiles-broadcast.service';
import { CacheService } from './shared/cache/cache.service';
import { HtmlTemplateService } from './templates/shared/html-template.service';
import { FeatureService } from './shared/feature/feature.service';
import { LicenseService } from './shared/license/license.service';
import { MemberService } from './member/shared/member.service';
import { InvoiceService } from './revenue/shared/invoice/invoice.service';
import { BatchService } from './revenue/shared/invoice/batch.service';
import { GuidService } from './shared/util/guid.service';
import { StyleService } from './shared/style/style.service';
import { VersionService } from './shared/version/version.service';
import { AkitaTrackingService } from './integrations/akita/shared/akita-tracking.service';
import { ConfigService } from './config/shared/config.service';
import { ConfigSettingService } from './config/shared/config-setting/config-setting.service';
import { AppLoadService } from './shared/app/app-load.service';
import { ListingService } from './listing/shared/listing.service';
import { ListingCategoryService } from './listing-catagories/listing-category.service';
import { MembershipLevelService } from './membership-level/shared/membership-level.service';
import { BillingService } from './revenue/shared/billing/billing.service';
import { QueryStringParamService } from './shared/http/query-string-param.service';
import { ProfileSearchV2ParameterService } from './profiles/shared/profile-search-v2-parameter.service';

// interceptor imports
import { AuthInterceptor } from './shared/http/interceptors/auth-interceptor';

//events
import { EventItemService } from './events/shared/items/event-item.service';
import { EventTypeService } from './events/shared/event-type/event-type.service';

//Users
import { UserService } from './users/shared/user.service';

//Files
import { FileService } from './shared/file/file.service';

//Reports
import { ReportFileService } from './reports/shared/report-file.service';

//#endregion

//#region Atlas Pipes
import { TitleCasePipe } from './shared/pipes/titlecase.pipe';
import { WlTimeZoneNamePipe } from './shared/pipes/timezone-name.pipe';
import { WlTimeZoneAbbrPipe } from './shared/pipes/timezone-abbr.pipe';
import { NegativeParensPipe } from './shared/pipes/negative-parens.pipe';
import { PhonePipe } from './shared/pipes/phone.pipe';
import { AbsoluteUrlPipe } from './shared/pipes/absolute-url/absolute-url.pipe';
import { EllipsisTruncaterPipe } from './shared/pipes/ellipsis-truncater.pipe';
import { PrettifyHtmlPipe } from './shared/pipes/prettify-html.pipe';
//#endregion

//#region Atlas Directives
import { WlModalBodyScrollToTopDirective } from './shared/directive/modal-body-scroll-to-top.directive';
import { WlModalCssClassDirective } from './shared/directive/modal-css-class.directive';
import { WlNgModelsValidDirective } from './shared/directive/ng-models-valid.directive';
import { WlScrollToTopDirective } from './shared/directive/back-to-top-link.directive';
import { WlTabsInModalDirective } from './shared/directive/wl-tabs-in-modal.directive';
import { WlOnEnterPressDirective } from './shared/directive/on-enter-press.directive';
import { InputGroupFocusRemoverDirective } from './shared/directive/input-group-focus-remover.directive';
//#endregion

//#region Atlas Components
import { AppComponent } from './app.component';
import { InitialTestComponent } from './test/initial-test.component';
import { MainContainerComponent } from './shared/container/main-container.component';
//import { UnauthenticatedComponent } from './shared/container/unauthenticated/unauthenticated.component';
import { UnauthenticatedComponent } from './shared/container/unauthenticated-psfy/unauthenticated-psfy.component';
import { TopNavComponent } from './shared/container/top-nav/top-nav.component';
import { WlModuleToolbarComponent } from './shared/container/toolbars/module-toolbar.component';
import { WlModuleEventsToolbarComponent } from './shared/container/toolbars/module/events/toolbar-module-events.component';
import { WlModuleProfilesToolbarComponent } from './shared/container/toolbars/module/profiles/toolbar-module-profiles.component';
import { ToolbarModuleRevenueComponent } from './shared/container/toolbars/module/revenue/toolbar-module-revenue.component';
import { NewInvoiceDialogComponent } from './shared/container/toolbars/module/revenue/new-invoice-dialog/new-invoice-dialog.component';
import { WlSelectEventComponent } from './events/shared/select-event/select-event.component';
import { WlEventDeleteModalComponent } from './events/shared/delete-event-modal/delete-event-modal.component';
import { LeftNavComponent } from './shared/container/left-nav/left-nav.component';
import { WorkspaceComponent } from './shared/container/workspace/workspace.component';
import { FooterComponent } from './shared/container/footer/footer.component';
import { NestedMenuComponent } from './shared/widgets/nested-menu/nested-menu.component';
import { NotificationComponent } from './shared/notification/notification.component';
import { DialogComponent } from './shared/dialog/dialog.component';
import { NoAccessComponent } from './shared/container/no-access/no-access.component';
import { ListBuilderComponent } from './shared/widgets/list-builder/list-builder.component';
import { ExpressionComponent } from './shared/widgets/expression/expression.component';
import { ExpressionSummaryComponent } from './shared/widgets/expression/expression-summary/expression-summary.component';
import { ExpressionOptionComponent } from './shared/widgets/expression/expression-option.component';
import { ExpressionOptionDropdownOptionComponent } from './shared/widgets/expression/expression-option-dropdown-option.component';
import { WlRadioCheckboxComponent } from './shared/widgets/radio-like-checkbox/radio-checkbox.component';
import { PagingControlsComponent } from './shared/widgets/paging-controls/paging-controls.component';
import { ProfileLookupComponent } from './profiles/profile-lookup/profile-lookup.component';

import { DateSearchExpressionComponent } from './shared/component/date-search-expression/date-search-expression.component';

//revenue
import { WlRevenueItemMultiSelectorComponent } from './revenue/revenue-item-multi-selector/revenue-item-multi-selector.component';
import { ClassTypeEditComponent } from './revenue/class-type/class-type-edit.component';
import { CreditCardChargeComponent } from './revenue/credit-card-charge/credit-card-charge.component';
import { InvoiceLookupComponent } from './revenue/invoice-lookup/invoice-lookup.component';
import { InvoiceSelectorComponent } from './revenue/invoice-selector/invoice-selector.component';
import { InvoiceSelectorFormComponent } from './revenue/invoice-selector/form/invoice-selector-form.component';
import { CreditCardTransactionsComponent } from './revenue/credit-card-transactions/credit-card-transactions.component';
import { InvoiceSelectorSummaryComponent } from './revenue/invoice-selector/invoice-selector-summary/invoice-selector-summary.component';
import { ArcDashboardComponent } from './revenue/arc-builder/arc-dashboard.component';
import { ArcService } from './revenue/shared/arc/arc.service';
import { RevenueItemService } from './revenue/shared/revenue-items/revenue-item.service';
import { ArcBuilderComponent } from './revenue/arc-builder/arc-builder.component';
import { InvoiceFinderComponent } from './revenue/invoice-finder/invoice-finder.component';
import { TransactionsGridComponent } from './revenue/transactions-grid/transactions-grid.component';
import { ExpandableTransactionDescriptionComponent } from './revenue/transactions-grid/expandable-transaction-description/expandable-transaction-description.component';
import { TransactionsGridEmailDialogComponent } from './revenue/transactions-grid/transactions-grid-email-dialog/transactions-grid-email-dialog.component';
import { TransactionsGridApplyPaymentsDialogComponent } from './revenue/transactions-grid/transactions-grid-apply-payments-dialog/transactions-grid-apply-payments-dialog.component';
import { UnappliedPaymentsGridComponent } from './revenue/transactions-grid/unapplied-payments-grid/unapplied-payments-grid.component';
import { CreateUnappliedPaymentDialogComponent } from './revenue/transactions-grid/create-unapplied-payment-dialog/create-unapplied-payment-dialog.component';
import { ApplyUnappliedPaymentsDialogComponent } from './revenue/transactions-grid/apply-unapplied-payments-dialog/apply-unapplied-payments-dialog.component';

//config
import { CurrencySymbolComponent } from './config/currency-symbol/currency-symbol.component';
import { DateTimeSelectorComponent } from './config/date-time-selector/date-time-selector.component';
import { WlCeuTypeDropdownComponent } from './config/ceu/ceu-type-dropdown/ceu-type-dropdown.component';

import { ConfirmSafeguardDialogComponent } from './shared/widgets/confirm-safeguard-dialog/confirm-safeguard-dialog.component';
import { CronBuilderComponent } from './shared/widgets/cron-builder/cron-builder.component';

import { ContactTypeService } from './contacts/contact-type/shared/contact-type.service';
import { ContactTypeSearchComponent } from './contacts/contact-type/search/contact-type-search.component';

//Dashboard
import { DashboardComponent } from './dashboard/dashboard.component';
import { DashboardMembershipActivityComponent } from './dashboard/membership-activity/membership-activity.component';
import { DashboardRetentionComponent } from './dashboard/membership-activity/retention.component';
import { DashboardEventsComponent } from './dashboard/events/events.component';
import { DashboardRevenueComponent } from './dashboard/revenue/revenue.component';
import { DashboardDuesAgingComponent } from './dashboard/revenue/dues-aging.component';
import { DashboardNonDuesAgingComponent } from './dashboard/revenue/non-dues-aging.component';
import { DashboardSalesOpportunitiesComponent } from './dashboard/dashboard-sales-opportunities/dashboard-sales-opportunities.component';
import { DashboardTasksComponent } from './dashboard/dashboard-tasks/dashboard-tasks.component';
import { DashboardNeoComponent } from './dashboard-neo/dashboard-neo.component';
import { DashboardNeoConfigComponent } from './dashboard-neo/dashboard-neo-config/dashboard-neo-config.component';
import { DashboardNeoMembershipActivityComponent } from './dashboard-neo/dashboard-neo-membership-activity/dashboard-neo-membership-activity.component';
import { DashboardNeoRetentionComponent } from './dashboard-neo/dashboard-neo-membership-activity/dashboard-neo-retention.component';
import { DashboardNeoRetentionDonutComponent } from './dashboard-neo/dashboard-neo-membership-activity/dashboard-neo-retention-donut.component';
import { DashboardNeoRetentionMonthlyComponent } from './dashboard-neo/dashboard-neo-membership-activity/dashboard-neo-retention-monthly.component';
import { DashboardNeoEventsComponent } from './dashboard-neo/dashboard-neo-events/dashboard-neo-events.component';
import { DashboardNeoRevenueComponent } from './dashboard-neo/dashboard-neo-revenue/dashboard-neo-revenue.component';
import { DashboardNeoMembershipReportsComponent } from './dashboard-neo/dashboard-neo-membership-activity/dashboard-neo-membership-reports.component';
import { DashboardNeoFinancialReportsComponent } from './dashboard-neo/dashboard-neo-revenue/dashboard-neo-financial-reports.component';
import { DashboardNeoReportsComponent } from './dashboard-neo/dashboard-neo-reports/dashboard-neo-reports.component';
import { DashboardNeoReportsConfigComponent } from './dashboard-neo/dashboard-neo-reports/dashboard-neo-reports-config/dashboard-neo-reports-config.component';
import { DashboardNeoDuesAgingComponent } from './dashboard-neo/dashboard-neo-revenue/dashboard-neo-dues-aging.component';
import { DashboardNeoNonDuesAgingComponent } from './dashboard-neo/dashboard-neo-revenue/dashboard-neo-non-dues-aging.component';
import { DashboardNeoSalesOpportunitiesComponent } from './dashboard-neo/dashboard-neo-sales-opportunities/dashboard-neo-sales-opportunities.component';
import { DashboardNeoTasksComponent } from './dashboard-neo/dashboard-neo-tasks/dashboard-neo-tasks.component';

//Events
import { EventsDashboardComponent } from './events/dashboard/events-dashboard.component';
import { EventDashboardComponent } from './events/dashboard/event-dashboard.component';
import { EventsSearchComponent } from './events/search/events-search.component';
import { EventSettingsComponent } from './events/settings/event-settings.component';
import { EventsContextSearchComponent } from './events/search/context-search-input/events-context-search-input.component';
import { EventDetailComponent } from './events/detail/event-detail.component';
import { EventSettingsGeneralComponent } from './events/settings/general/event-settings-general.component';
import { EventSettingsItemsComponent } from './events/settings/items/event-settings-items.component';
import { EventSettingsPaymentMethodsComponent } from './events/settings/payment-methods/event-settings-payment-methods.component';
import { EventSettingsRegistrationSettingsComponent } from './events/settings/registration-settings/event-settings-registration-settings.component';
import { EventSettingsSponsorsComponent } from './events/settings/sponsors/event-settings-sponsors.component';
import { EventRegistrationDashboardComponent } from './events/settings/registration/registration-dashboard.component';
import { EventNewRegistrationComponent } from './events/settings/registration/new-registration.component';
import { EventLeftNavComponent } from './events/left-nav/event-left-nav.component';
import { EventSettingsRegistrationFieldsComponent } from './events/settings/registration-fields/event-settings-registration-fields.component';
import { EventSettingsRegistrationDropDownValuesComponent } from './events/settings/registration-fields/drop-down-values/event-settings-registration-drop-down-values.component';
import { EventItemCreditTypeListComponent } from './events/settings/items/edit/ceu-edit/event-item-credit-type-list.component';
import { EventSettingsRegistrationFieldsFieldRowComponent } from './events/settings/registration-fields/field-row/event-settings-registration-fields-field-row.component';
import { EventSettingsPromoCodesComponent } from './events/settings/promo-codes/event-settings-promo-codes.component';
import { EventCopyComponent } from './events/copy/event-copy.component';
import { EventsNewComponent } from './events/new/events-new.component';
import { EventTypeEditComponent } from './events/event-type/event-type-edit.component';
import { ImportRegistationsComponent } from './events/import/import-registrations/import-registations.component';
import { EventSettingsBenefitsComponent } from './events/settings/benefits/event-settings-benefits.component';
import { EventsService } from './events/shared/events.service';
import { ProfileStatusComponent } from './profiles/profile-status/profile-status.component';

import { RegistrationWizardComponent } from './events/registration/wizard/registration-wizard.component';
import { RegistrationWizardContactComponent } from './events/registration/wizard/contact/registration-wizard-contact.component';
import { RegistrationWizardAttendeeComponent } from './events/registration/wizard/attendees/attendee/registration-wizard-attendee.component';
import { WLRegistrationWizardAttendeeTabNav } from './events/registration/wizard/attendees/attendee-tab-nav/registration-wizard-attendee-tab-nav.component';
import { RegistrationWizardAttendeesComponent } from './events/registration/wizard/attendees/registration-wizard-attendees.component';
import { RegistrationWizardItemsComponent } from './events/registration/wizard/items/registration-wizard-items.component';
import { RegistrationWizardPaymentInfoComponent } from './events/registration/wizard/payment-info/registration-wizard-payment-info.component';
import { RegistrationWizardSponsorComponent } from './events/registration/wizard/sponsor/registration-wizard-sponsor.component';
import { RegistrationWizardModificationOptionsComponent } from './events/registration/wizard/modification-options/registration-wizard-modification-options.component';
import { RegistrationManagementComponent } from './events/registration/management/registration-management.component';
import { RegistrationCustomFieldComponent } from './events/registration/shared/custom-fields/custom-field/registration-custom-field.component';
import { RegistrationCustomFieldsComponent } from './events/registration/shared/custom-fields/registration-custom-fields.component';
import { RegistrationDeleteComponent } from './events/registration/delete/registration-delete.component';
import { RegistrationManagementSelectorComponent } from './events/registration/management/selector/registration-management-selector.component';
import { AttendeeManagementComponent } from './events/attendee/management/attendee-management.component';
import { EventRegistrationFieldDropdownValueCreditTypeListComponent } from './events/settings/registration-fields/drop-down-values/ceu-edit/event-settings-registration-drop-down-value-credit-type-list.component';
import { SubmissionsComponent } from './events/submissions/submissions.component';
import { SubmissionFormSettingsComponent } from './events/submissions/submission-form-settings/submission-form-settings.component';
import { SubmissionListComponent } from './events/submissions/submission-list/submission-list.component';
import { SubmissionReviewModalComponent } from './events/submissions/submission-review-modal/submission-review-modal.component';

// Events V4 Components

//Benefit Componenets
import { BenefitMgtComponent } from './benefits/benefit-mgt/benefit-mgt.component';
import { BenefitMgtAdvancedSearchComponent } from './benefits/benefit-mgt/benefit-mgt-advanced-search/benefit-mgt-advanced-search.component';
import { ProfileBenefitMgtComponent } from './benefits/profile-benefit-mgt/profile-benefit-mgt.component';
import { BenefitAddComponent } from './benefits/shared/benefit-add/benefit-add.component';
import { BenefitFlagModalComponent } from './benefits/shared/benefit-flag/benefit-flag.component';

// mass comm components
import { MassCommMessageAdminComponent } from './communication/mass-comm-message-admin/mass-comm-message-admin.component';
import { MassCommMessageAdminDraftsComponent } from './communication/mass-comm-message-admin/drafts/mass-comm-message-admin-drafts.component';
import { MassCommMessageAdminRecurringComponent } from './communication/mass-comm-message-admin/recurring/mass-comm-message-admin-recurring.component';
import { MassCommMessageAdminScheduledMessagesComponent } from './communication/mass-comm-message-admin/scheduled/mass-comm-message-admin-scheduled-messages.component';
import { MassCommMessageAdminSentMessagesComponent } from './communication/mass-comm-message-admin/sent/mass-comm-message-admin-sent-messages.component';
import { MassCommMessageEditComponent } from './communication/mass-comm-message-edit/mass-comm-message-edit.component';
import { MassCommMessageEditMessageComponent } from './communication/mass-comm-message-edit/message/mass-comm-message-edit-message.component';
import { MassCommMessageEditProfilesComponent } from './communication/mass-comm-message-edit/profiles/mass-comm-message-edit-profiles.component';
import { MassCommMessageEditProfilesAddManualComponent } from './communication/mass-comm-message-edit/profiles/add-manual/mass-comm-message-edit-profiles-add-manual.component';
import { MassCommMessageEditProfilesEmailTypeConfigComponent } from './communication/mass-comm-message-edit/profiles/email-type-config/mass-comm-message-edit-profiles-email-type-config.component';
import { MassCommMessageEditSendComponent } from './communication/mass-comm-message-edit/send/mass-comm-message-edit-send.component';
import { MassCommMessageEditSendComponentAttachments } from './communication/mass-comm-message-edit/send/attachments/mass-comm-message-edit-send-attachments.component';
import { MassCommMessageEditAnalyticsComponent } from './communication/mass-comm-message-edit/analytics/mass-comm-message-edit-analytics.component';
import { MassCommMessageEditAnalyticsDeliveryDetailsComponent } from './communication/mass-comm-message-edit/analytics/delivery-details/mass-comm-message-edit-analytics-delivery-details.component';
import { MassCommMessageEditAnalyticsClickDetailsComponent } from './communication/mass-comm-message-edit/analytics/click-details/mass-comm-message-edit-analytics-click-details.component';
import { MassCommService } from './communication/shared/mass-comm.service';
import { MassCommMessageHtmlEditorDialogComponent } from './communication/mass-comm-message-edit/message/mass-comm-message-html-editor-dialog/mass-comm-message-html-editor-dialog.component';
import { MassCommMessageAdminAnalyticsComponent } from './communication/mass-comm-message-admin/mass-comm-message-admin-analytics/mass-comm-message-admin-analytics.component';
import { MassCommUndeliverableEmailGridComponent } from './communication/mass-comm-message-admin/mass-comm-message-admin-analytics/mass-comm-undeliverable-email-grid/mass-comm-undeliverable-email-grid.component';
import { MemberEmailEngagementGridComponent } from './communication/mass-comm-message-admin/mass-comm-message-admin-analytics/member-email-engagement-grid/member-email-engagement-grid.component';
import { MassCommClickedEmailLinksGridComponent } from './communication/mass-comm-message-admin/mass-comm-message-admin-analytics/mass-comm-clicked-email-links-grid/mass-comm-clicked-email-links-grid.component';
import { MassCommProfileSelectorDialogComponent } from './communication/mass-comm-message-edit/analytics/mass-comm-profile-selector-dialog/mass-comm-profile-selector-dialog.component';
import { MassCommSubscriptionTrackingComponent } from './communication/mass-comm-message-admin/mass-comm-subscription-tracking/mass-comm-subscription-tracking.component';

import { PublicLinksComponent } from './communication/public-links/public-links.component';
import { PublicLinksUpgradeDialogComponent } from './communication/public-links/public-links-upgrade-dialog/public-links-upgrade-dialog.component';
import { PublicLinksProgressDialogComponent } from './communication/public-links/public-links-progress-dialog/public-links-progress-dialog.component';
// content management
import { ContentManagementLink } from './communication/content-management-link/content-management-link.component';

//integration components
import { CardConnectTransComponent } from './integrations/card-connect/card-connect-trans/card-connect-trans.component';
import { CardConnectService } from './integrations/card-connect/shared/card-connect.service';

import { VoterVoiceService } from './integrations/voter-voice/shared/voter-voice.service';
import { CreditCardGatewayService } from './revenue/shared/credit-card-gateway/credit-card-gateway.service';
import { WeeblyService } from './integrations/weebly/shared/weebly.service';
import { MailChimpSyncComponent } from './integrations/mailchimp/mailchimp-sync.component';
import { MailChimpSyncService } from './integrations/mailchimp/shared/mailchimp-sync.service';
import { MailChimpSettingsService } from './integrations/mailchimp/shared/mailchimp-settings.service';

// profile components
import { ProfileSearchComponent } from './profiles/search/profile-search.component';
import { ProfileSearchManageColumnsComponent } from './profiles/search/profile-search-manage-columns/profile-search-manage-columns.component';
import { ProfileSearchModalComponent } from './profiles/search/profile-search-modal.component';
import { ProfileSearchAdvancedOptionsComponent } from './profiles/search/profile-search-advanced-options/profile-search-advanced-options.component';
import { ProfileSearchSummaryComponent } from './profiles/search/profile-search-summary/profile-search-summary.component';
import { MemberActivitySearchActionComponent } from './profiles/search/search-actions/member-activity-search-action/member-activity-search-action.component';
import { AssignUserSearchActionComponent } from './profiles/search/search-actions/assign-user-search-action/assign-user-search-action.component';
import { SetStatusSearchActionComponent } from './profiles/search/search-actions/set-status-search-action/set-status-search-action.component';
import { CreateContactSearchActionComponent } from './profiles/search/search-actions/create-contact-search-action/create-contact-search-action.component';
import { CreateTaskSearchActionComponent } from './profiles/search/search-actions/create-task-search-action/create-task-search-action.component';
import { ComonDataViewSelectorComponent } from './profiles/shared/common-data-view-selector/common-data-view-selector.component';
import { ProfileFinderComponent } from './profiles/profile-finder/profile-finder-component';
import { AddReferralDialogComponent } from './profiles/profile-view/referral-report/add-referral-dialog/add-referral-dialog.component';
import { ProfileViewComponent } from './profiles/profile-view/profile-view.component';
import { ProfileViewRevenueComponent } from './profiles/profile-view/revenue/revenue.component';
import { ProfileViewAffiliationComponent } from './profiles/profile-view/affiliation/affiliation.component';
import { ProfileViewCustomFieldComponent } from './profiles/profile-view/custom-field/custom-field.component';
import { ProfileViewMemberActivityComponent } from './profiles/profile-view/member-activity/member-activity.component';
import { ProfileViewEventsComponent } from './profiles/profile-view/events/profile-view-events.component';
import { ProfileViewPrimaryRelationsComponent } from './profiles/profile-view/primary-relations/primary-relations.component';
import { ProfileViewRecentContactsComponent } from './profiles/profile-view/recent-contacts/recent-contacts.component';
import { ProfileViewReferralReportComponent } from './profiles/profile-view/referral-report/referral-report.component';
import { ProfileViewBenefitsComponent } from './profiles/profile-view/benefits/benefits.component';
import { ProfileAddAsMemberComponent } from './profiles/profile-view/profile-add-as-member/profile-add-as-member.component';
import { ProfileNotificationsComponent } from './profiles/profile-view/notifications/notifications.component';
import { ProfileViewWebContentComponent } from './profiles/profile-view/profile-view-web-content/profile-view-web-content.component';
import { ProfileViewCertificationsComponent } from './profiles/profile-view/profile-view-certifications/profile-view-certifications.component';
import { ProfileViewSalesOpportunitiesComponent } from './profiles/profile-view/profile-view-sales-opportunities/profile-view-sales-opportunities.component';
import { ProfileViewTasksComponent } from './profiles/profile-view/profile-view-tasks/profile-view-tasks.component';
import { ProfileViewVoterVoiceComponent } from './profiles/profile-view/profile-view-voter-voice/profile-view-voter-voice.component';
import { ProfileViewCommitteesComponent } from './profiles/profile-view/profile-view-committees/profile-view-committees.component';
import { ProfileAddToCommitteeComponent } from './profiles/profile-view/profile-add-to-committee/profile-add-to-committee.component';
import { ProfileHistoryComponent } from './profiles/profile-view/profile-history/profile-history.component';
import { ProfileEditComponent } from './profiles/profile-edit/profile-edit.component';
import { ProfileEditGeneralComponent } from './profiles/profile-edit/profile-edit-general/profile-edit-general.component';
import { ProfileEditMembershipComponent } from './profiles/profile-edit/profile-edit-membership/profile-edit-membership.component';
import { ProfileEditAddressComponent } from './profiles/profile-edit/profile-edit-address/profile-edit-address.component';
import { ProfileEditAddressListComponent } from './profiles/profile-edit/profile-edit-address/profile-edit-address-list.component';
import { ProfileEditPhoneListComponent } from './profiles/profile-edit/profile-edit-phone/profile-edit-phone-list.component';
import { ProfileEditEmailsComponent } from './profiles/profile-edit/profile-edit-emails/profile-edit-emails.component';
import { ProfileEditSocialMediaComponent } from './profiles/profile-edit/profile-edit-social-media/profile-edit-social-media.component';
import { ProfileEditAffiliationsComponent } from './profiles/profile-edit/profile-edit-affiliations/profile-edit-affiliations.component';
import { ProfileEditWebPortalCredentialsComponent } from './profiles/profile-edit/profile-edit-web-portal-credentials/profile-edit-web-portal-credentials.component';
import { ProfileEditListingListComponent } from './profiles/profile-edit/profile-edit-listing/profile-edit-listing-list.component';
import { ProfileEditListingComponent } from './profiles/profile-edit/profile-edit-listing/profile-edit-listing.component';
import { ProfileEditBillingListComponent } from './profiles/profile-edit/profile-edit-billing/profile-edit-billing-list.component';
import { ProfileEditBillingComponent } from './profiles/profile-edit/profile-edit-billing/profile-edit-billing.component';
import { ProfileEditRelationshipComponent } from './profiles/profile-edit/profile-edit-relationship/profile-edit-relationship.component';
import { ProfileEditCustomFieldsComponent } from './profiles/profile-edit/profile-edit-custom-fields/profile-edit-custom-fields.component';
import { ProfileEditProfileTypeComponent } from './profiles/profile-edit/profile-edit-profile-type/profile-edit-profile-type.component';
import { ProfileEditDeleteComponent } from './profiles/profile-edit/profile-edit-delete/profile-edit-delete.component';
import { NaaProfileTypeEditComponent } from './profiles/profile-edit/profile-edit-profile-type/naa-profile-type-edit/naa-profile-type-edit.component';
import { ProfileTypeManagementCompanyEditComponent } from './profiles/profile-edit/profile-edit-profile-type/naa-profile-type-edit/profile-type-management-company-edit/profile-type-management-company-edit.component';
import { ProfileTypeOwnerEditComponent } from './profiles/profile-edit/profile-edit-profile-type/naa-profile-type-edit/profile-type-owner-edit/profile-type-owner-edit.component';
import { ProfileTypePropertyEditComponent } from './profiles/profile-edit/profile-edit-profile-type/naa-profile-type-edit/profile-type-property-edit/profile-type-property-edit.component';
import { NahbProfileTypeEditComponent } from './profiles/profile-edit/profile-edit-profile-type/nahb-profile-type-edit/nahb-profile-type-edit.component';
import { ProfileEditNotesComponent } from './profiles/profile-edit/profile-edit-notes/profile-edit-notes.component';
import { ProfileEditEmbeddedVideoComponent } from './profiles/profile-edit/profile-edit-embedded-video/profile-edit-embedded-video.component';
import { ProfileRelationshipGridComponent } from './profiles/profile-relationships/profile-relationship-grid/profile-relationship-grid.component';
import { ProfileRelationshipsComponent } from './profiles/profile-relationships/profile-relationships.component';
import { ProfileRelateExistingComponent } from './profiles/profile-relate-existing/profile-relate-existing.component';
import { ProfileNewComponent } from './profiles/profile-new/profile-new.component';
import { ProfileContactsComponent } from './profiles/profile-contacts/profile-contacts.component';
import { ProfileContactCardComponent } from './profiles/profile-contacts/profile-contact-card/profile-contact-card.component';
import { ProfileContactEditComponent } from './profiles/profile-contacts/profile-contact-edit/profile-contact-edit.component';
import { ProfileTransactionsComponent } from './profiles/profile-transactions/profile-transactions.component';
import { ProfileMemberActivityEditingComponent } from './profiles/profile-member-activity-editing/profile-member-activity-editing.component';
import { ProfileWebContentComponent } from './profiles/profile-web-content/profile-web-content.component';
import { ProfileSalesOpportunitiesComponent } from './profiles/profile-sales-opportunities/profile-sales-opportunities.component';
import { ProfileTasksSearchComponent } from './profiles/profile-tasks/profile-tasks-search.component';
import { ProfileContactRecordsComponent } from './profiles/profile-contact-records/profile-contact-records.component';
import { ProfileContactsAnalyticsDialogComponent } from './profiles/profile-contacts/profile-contacts-analytics-dialog/profile-contacts-analytics-dialog.component';
import { ProfileSelectorResultsComponent } from './profiles/search/profile-selector-results/profile-selector-results.component';
import { ProfileEditDropActivityDialogComponent } from './profiles/profile-edit/profile-edit-affiliations/profile-edit-drop-activity-dialog/profile-edit-drop-activity-dialog.component';

//Admin compoents
import { HttpRequestItemsComponent } from './admin/http-request-items/http-request-items.component';
import { AdminSettingsComponent } from './admin/settings/admin-settings.component';

//Form Builder Components
import { FormBuilderComponent } from './forms/form-builder.component';
import { FormBuilderEditElementComponent } from './forms/form-builder-edit-element/form-builder-edit-element.component';
import { FormBuilderSocialMediaEditElementComponent } from './forms/form-builder-social-media-element/form-builder-social-media-edit-element.component';
import { FormBuilderSocialMediaDisplayElementComponent } from './forms/form-builder-social-media-element/form-builder-social-media-display-element.component';
import { FormBuilderProfileEditElementComponent } from './forms/form-builder-profile-element/form-builder-profile-edit-element.component';
import { FormBuilderProfileDisplayElementComponent } from './forms/form-builder-profile-element/form-builder-profile-display-element.component';
import { FormBuilderListingDisplayElementComponent } from './forms/form-builder-listing-element/form-builder-listing-display-element.component';

import { FormBuilderListingEditElementComponent } from './forms/form-builder-listing-element/form-builder-listing-edit-element.component';

import { FormBuilderProfileAddressDisplayElementComponent } from './forms/form-builder-profile-address-element/form-builder-profile-address-display-element.component';
import { FormBuilderProfileAddressEditElementComponent } from './forms/form-builder-profile-address-element/form-builder-profile-address-edit-element.component';
import { FormBuilderSettingsComponent } from './forms/form-builder-settings/form-builder-settings.component';
import { FormBuilderHbaProfileDataEditElementComponent } from './forms/vertical/form-builder-hba-profile-data-element/form-builder-hba-profile-data-edit-element.component';
import { FormBuilderHbaProfileDataDisplayElementComponent } from './forms/vertical/form-builder-hba-profile-data-element/form-builder-hba-profile-data-display-element.component';
import { FormBuilderMembershipLevelDisplayElementComponent } from './forms/form-builder-membership-level-element/form-builder-membership-level-display-element.component';
import { FormBuilderMembershipLevelEditElementComponent } from './forms/form-builder-membership-level-element/form-builder-membership-level-edit-element.component';
import { FormsDashboardComponent } from './forms/forms-dashboard.component';
import { FormBuilderAffiliationChecklistEditElementComponent } from './forms/form-builder-affiliation-checklist-element/form-builder-affiliation-checklist-edit-element.component';
import { FormBuilderAffiliationChecklistDisplayElementComponent } from './forms/form-builder-affiliation-checklist-element/form-builder-affiliation-checklist-display-element.component';
import { FormBuilderFileUploadEditElementComponent } from './forms/form-builder-upload-file-element/form-builder-upload-file-edit-element.component';
import { FormBuilderFileUploadDisplayElementComponent } from './forms/form-builder-upload-file-element/form-builder-upload-file-display-element.component';

import {
  FormBuilderEmailOptIntEditElementComponent,
  FormBuilderEmailOptInDisplayElementComponent,
} from './forms/form-builder-email-optin-element';

//Web Content
import { WebContentTypeService } from './web-content/shared/web-content-type.service';
import { ManageWebContentComponent } from './web-content/manage-web-content/manage-web-content.component';
import { WebContentSearchParamsComponent } from './web-content/web-content-search-params/web-content-search-params.component';
import { WebContentSearchGridComponent } from './web-content/web-content-search-grid/web-content-search-grid.component';
import { WebContentEditDialogComponent } from './web-content/web-content-edit-dialog/web-content-edit-dialog.component';
import { WebContentEditComponent } from './web-content/web-content-edit/web-content-edit.component';

//Invoices
import { InvoiceBuilderComponent } from './invoices/invoice-builder/invoice-builder.component';
import { InvoiceLineItemsListComponent } from './invoices/invoice-builder/invoice-line-items-list/invoice-line-items-list.component';
import { InvoiceLineItemComponent } from './invoices/invoice-builder/invoice-line-item/invoice-line-item.component';
import { InvoicePaymentsListComponent } from './invoices/invoice-builder/invoice-payments-list/invoice-payments-list.component';
import { InvoicePaymentComponent } from './invoices/invoice-builder/invoice-payment/invoice-payment.component';
import { InvoicePaymentHeaderComponent } from './invoices/invoice-builder/invoice-payment-header/invoice-payment-header.component';
import { InvoicePaymentEditComponent } from './invoices/invoice-builder/invoice-payment-edit/invoice-payment-edit.component';
import { InvoiceAdjustmentComponent } from './invoices/invoice-builder/invoice-adjustment/invoice-adjustment.component';
import { InvoiceAdjustmentsListComponent } from './invoices/invoice-builder/invoice-adjustments-list/invoice-adjustments-list.component';
import { InvoiceAdjustmentEditComponent } from './invoices/invoice-builder/invoice-adjustment-edit/invoice-adjustment-edit.component';
import { InvoiceSummaryComponent } from './invoices/invoice-builder/invoice-summary/invoice-summary.component';
import { InvoiceDetailsComponent } from './invoices/invoice-builder/invoice-details/invoice-details.component';
import { InvoiceDeleteComponent } from './invoices/invoice-builder/invoice-delete/invoice-delete.component';
import { NewMemberInvoiceComponent } from './new-member/new-member-invoice/new-member-invoice.component';
import { InvoiceEditComponent } from './invoices/invoice-edit/invoice-edit.component';
import { InvoiceGeneratorComponent } from './revenue/invoice-generator/invoice-generator.component';
import { BillingAmountUpdateComponent } from './revenue/billing-amount-update/billing-amount-update.component';
import { MultiInvoicePaymentComponent } from './revenue/multi-invoice-payment/multi-invoice-payment.component';
import { BatchPaymentDialogComponent } from './shared/container/toolbars/module/revenue/batch-payment-dialog/batch-payment-dialog.component';
import { CreditCardPaymentComponent } from './invoices/invoice-builder/invoice-payment/credit-card-payment/credit-card-payment.component';
import { InvoiceModificationComponent } from './invoices/invoice-modification/invoice-modification.component';

//reports
import { ReportFilesComponent } from './reports/report-files/report-files.component';
import { ReportQueryToolComponent } from './reports/report-query-tool/report-query-tool.component';
import { ReportSavedQueriesComponent } from './reports/report-saved-queries/report-saved-queries.component';
import { QueryToolAdvancedComponent } from './reports/report-query-tool/query-tool-advanced/query-tool-advanced.component';
import { QueryToolSelectFieldsComponent } from './reports/report-query-tool/query-tool-select-fields/query-tool-select-fields.component';
import { QueryToolSetFiltersComponent } from './reports/report-query-tool/query-tool-set-filters/query-tool-set-filters.component';
import { QueryToolSetSortOrderComponent } from './reports/report-query-tool/query-tool-set-sort-order/query-tool-set-sort-order.component';
import { QueryToolViewResultsComponent } from './reports/report-query-tool/query-tool-view-results/query-tool-view-results.component';
import { ReportsQueryToolSavedQueriesComponent } from './reports/reports-query-tool-saved-queries/reports-query-tool-saved-queries.component';
import { ReportsQueryToolDateParamDialogComponent } from './reports/reports-query-tool-saved-queries/reports-query-tool-date-param-dialog/reports-query-tool-date-param-dialog.component';
import { DataDictionaryComponent } from './reports/data-dictionary/data-dictionary.component';
import { ReportStandardReportFilesComponent } from './reports/report-files/report-standard-report-files/report-standard-report-files.component';
import { ReportCustomReportFilesComponent } from './reports/report-files/report-custom-report-files/report-custom-report-files.component';
import { NAHBExportComponent } from './reports/nahb-export/nahb-export.component';
import { NahbExportValidationModalComponent } from './reports/nahb-export/nahb-export-validation-modal/nahb-export-validation-modal.component';

// Certification
import { CeuEditDialogComponent } from './certification/ceu-edit-dialog/ceu-edit-dialog.component';
import { CertificationEditDialogComponent } from './certification/certification-edit-dialog/certification-edit-dialog.component';

//#region Sales Opportunities
import { SalesOpportunityEditDialogComponent } from './sales-opportunities/sales-opportunity-edit-dialog/sales-opportunity-edit-dialog.component';
import { SalesOpportunityEditComponent } from './sales-opportunities/sales-opportunity-edit/sales-opportunity-edit.component';
import { SalesOpportunitySearchParamsComponent } from './sales-opportunities/sales-opportunity-search-params/sales-opportunity-search-params.component';
import { SalesOpportunitySearchGridComponent } from './sales-opportunities/sales-opportunity-search-grid/sales-opportunity-search-grid.component';
//#endregion

//#region Contact Records
import { ContactRecordSearchParamsComponent } from './contacts/contact-record/contact-record-search-params/contact-record-search-params.component';
import { ContactRecordSearchGridComponent } from './contacts/contact-record/contact-record-search-grid/contact-record-search-grid.component';
//#endregion

//#region Projects
import { TaskSearchParamsComponent } from './tasks/task-search-params/task-search-params.component';
import { TaskSearchGridComponent } from './tasks/task-search-grid/task-search-grid.component';
import { TaskEditDialogComponent } from './tasks/task-edit-dialog/task-edit-dialog.component';
import { TaskEditComponent } from './tasks/task-edit/task-edit.component';
import { TaskGenerateDialogComponent } from './tasks/task-generate-dialog/task-generate-dialog.component';
//#endregion

// #region Committees
import { CommitteeEditComponent } from './committees/committee-edit/committee-edit.component';
import { CommitteeDetailsEditComponent } from './committees/committee-edit/committee-details-edit/committee-details-edit.component';
import { CommitteeMembersComponent } from './committees/committee-edit/committee-members/committee-members.component';
import { CommitteeMembersSearchParamsComponent } from './committees/committee-edit/committee-members/committee-members-search-params/committee-members-search-params.component';
import { CommitteeMembersSearchGridComponent } from './committees/committee-edit/committee-members/committee-members-search-grid/committee-members-search-grid.component';
import { CommitteeMeetingsComponent } from './committees/committee-edit/committee-meetings/committee-meetings.component';
import { CommitteeMeetingsSearchParamsComponent } from './committees/committee-edit/committee-meetings/committee-meetings-search-params/committee-meetings-search-params.component';
import { CommitteeMeetingsSearchGridComponent } from './committees/committee-edit/committee-meetings/committee-meetings-search-grid/committee-meetings-search-grid.component';
import { CommitteeMemberEditDialogComponent } from './committees/committee-member-edit-dialog/committee-member-edit-dialog.component';
import { CommitteeMemberEditComponent } from './committees/committee-member-edit/committee-member-edit.component';
import { CommitteeMemberProfileGridComponent } from './committees/committee-member-group-add-dialog/committee-member-profile-grid/committee-member-profile-grid.component';
import { CommitteeMemberGroupAddDialogComponent } from './committees/committee-member-group-add-dialog/committee-member-group-add-dialog.component';

import { CommitteeSearchParamsComponent } from './committees/committee-search-params/committee-search-params.component';
import { CommitteeSearchGridComponent } from './committees/committee-search-grid/committee-search-grid.component';
import { CommitteeMemberSearchActionsComponent } from './committees/committee-edit/committee-members/committee-member-search-actions/committee-member-search-actions.component';
import { CommitteeAddDialogComponent } from './committees/committee-add-dialog/committee-add-dialog.component';
import { CommitteeMeetingDetailsEditComponent } from './committees/committee-meeting-edit/committee-meeting-details-edit/committee-meeting-details-edit.component';
import { CommitteeMeetingAgendaEditComponent } from './committees/committee-meeting-edit/committee-meeting-agenda-edit/committee-meeting-agenda-edit.component';
import { CommitteeMeetingEditComponent } from './committees/committee-meeting-edit/committee-meeting-edit.component';
import { CommitteeMeetingMinutesEditComponent } from './committees/committee-meeting-edit/committee-meeting-minutes-edit/committee-meeting-minutes-edit.component';
import { CommitteeMeetingAttendanceEditComponent } from './committees/committee-meeting-edit/committee-meeting-attendance-edit/committee-meeting-attendance-edit.component';
import { AutoFocusDirective } from './shared/directive/auto-focus.directive';
import { CommitteeMeetingAttendanceGridComponent } from './committees/committee-meeting-edit/committee-meeting-attendance-edit/committee-meeting-attendance-grid/committee-meeting-attendance-grid.component';
import { CommitteeMeetingAttendanceGuestAddDialogComponent } from './committees/committee-meeting-edit/committee-meeting-attendance-edit/committee-meeting-attendance-guest-add-dialog/committee-meeting-attendance-guest-add-dialog.component';
import { S3FilenamePipe } from './shared/pipes/s3-filename.pipe';
import { CommitteeSearchComponent } from './committees/committee-search/committee-search.component';
import { CouponSearchComponent } from './coupons/coupon-search/coupon-search.component';
import { CouponSearchGridComponent } from './coupons/coupon-search/coupon-search-grid/coupon-search-grid.component';
import { CouponSearchParamsComponent } from './coupons/coupon-search/coupon-search-params/coupon-search-params.component';
import { CouponAddDialogComponent } from './coupons/coupon-add-dialog/coupon-add-dialog.component';
// #endregion

// #region Banner Ads
import { BannerAdSearchComponent } from './banner-ads/banner-ad-search/banner-ad-search.component';
import { BannerAdSearchParamsComponent } from './banner-ads/banner-ad-search/banner-ad-search-params/banner-ad-search-params.component';
import { BannerAdSearchGridComponent } from './banner-ads/banner-ad-search/banner-ad-search-grid/banner-ad-search-grid.component';
import { BannerAdEditDialogComponent } from './banner-ads/banner-ad-edit-dialog/banner-ad-edit-dialog.component';
import { BannerAdEditComponent } from './banner-ads/banner-ad-edit/banner-ad-edit.component';
// #endregion

import {
  NewsArticlesSearchComponent,
  NewsArticlesSearchGridComponent,
  NewsArticlesSearchParamsComponent,
  NewsArticleEditDialogComponent,
} from './news-articles';
import { ThirdPartySharedModule } from './shared/module/third-party-shared.module';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { AddressTypeService } from './admin/codes/shared/address-type.service';
import { QboAuthenticationLandingComponent } from './shared/component/qbo-authentication-landing/qbo-authentication-landing.component';

//#region Not currently lazy-loaded feature modules
import { RevenueModule } from './revenue/revenue.module';
import { FormBuilderModule } from './form-builder/form-builder.module';
import { EcomModule } from './ecommerce/ecommerce.module';
import { ProfileSearchService } from './profiles/search';
import { TitleStrategy } from '@angular/router';
import { CustomPageTitleStrategy } from './shared/title/custom-title-strategy';
import { PaymentSourceFinancialService } from './revenue/shared/payment-source/financial-service-payment-source.service';
import { InvoiceFinderWriteOffComponent } from './revenue/invoice-finder/invoice-finder-write-off/invoice-finder-write-off.component';
import { MultipleProfileInvoiceModalComponent } from './profiles/search/search-actions/multiple-profile-invoice-modal/multiple-profile-invoice-modal.component';
import { QueryToolViewSqlComponent } from './reports/report-query-tool/query-tool-view-sql/query-tool-view-sql.component';
import { QueryToolSaveQueryModalComponent } from './reports/report-query-tool/query-tool-save-query-modal/query-tool-save-query-modal.component';
import { ReportsRunAReportComponent } from './reports/reports-run-a-report/reports-run-a-report.component';
import { MassCommMessageEditAnalyticsResendDialogComponent } from './communication/mass-comm-message-edit/analytics/mass-comm-message-edit-analytics-resend-dialog/mass-comm-message-edit-analytics-resend-dialog.component';
import { ProfileReferralValueReportComponent } from './profiles/profile-referral-value-report/profile-referral-value-report.component';
import { BannerAdsEmbedCodeDialogComponent } from './banner-ads/banner-ads-embed-code-dialog/banner-ads-embed-code-dialog.component';
import { MassCommParameterService } from './communication/shared/mass-comm-parameter.service';
import { EventsV4Module } from './events-v4/events-v4.module';
import { WlEventsV4ToolbarBroadcastService } from './shared/container/toolbars/module/events-v4/broadcast';
import { UserPasswordChangeComponent } from './users/user-password-change/user-password-change.component';

import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
//#endregion

// export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

//prettier-ignore
export const atlasProviders = [
  { provide: TitleStrategy, useClass: CustomPageTitleStrategy },
  EnvironmentService,
  UtilService,
  LogService,
  LocalStorageService,
  AppLoadService,
  { provide: APP_INITIALIZER, useFactory: init_app, deps: [AppLoadService], multi: true },
  { provide: ErrorHandler, useClass: ExceptionService },
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
  PlatformService,
  { provide: MissingTranslationHandler, useClass: TranslationHandlerService },
  { provide: LocationStrategy, useClass: PathLocationStrategy },
  TranslationService, //our implementation
  BreadcrumbService,
  UtilService,
  MessageService,
  LocalStorageService,
  AuthService,
  AuthGuard,
  WlCanDeactivateRouteGuard,
  ApiService,
  ServiceApiService,
  HttpRequestService,
  NotificationService,
  DialogService,
  LogService,
  BreadcrumbService,
  DatagridService,
  ConfigService,
  ConfigSettingService,
  EventItemService,
  EventTypeService,
  UserService,
  ProfileService,
  ProfileSearchService,
  PortalAuthService,
  ProfileStatusService,
  RelationTypeService,
  NavigationService,
  DeviceUtilService,
  CeuService,
  ContactTypeService,
  WlEventsToolbarBroadcastService,
  WlProfilesToolbarBroadcastService,
  WlEventsV4ToolbarBroadcastService,
  CacheService,
  HtmlTemplateService,
  FeatureService,
  LicenseService,
  MassCommService,
  MassCommParameterService,
  MemberService,
  VoterVoiceService,
  CardConnectService,
  InvoiceService,
  CreditCardGatewayService,
  WeeblyService,
  MailChimpSyncService,
  MailChimpSettingsService,
  GuidService,
  AddressTypeService,
  EnvironmentService,
  VersionService,
  StyleService,
  ArcService,
  RevenueItemService,
  AkitaTrackingService,
  EventsService,
  WebContentTypeService,
  ListingService,
  ListingCategoryService,
  MembershipLevelService,
  BillingService,
  BatchService,
  QueryStringParamService,
  FileService,
  ReportFileService,
  ConfirmationService,
  PaymentSourceFinancialService,
  ProfileSearchV2ParameterService,
];

//prettier-ignore
export const atlasDeclarations = [
  InitialTestComponent,
  TitleCasePipe,
  NegativeParensPipe,
  PhonePipe,
  AbsoluteUrlPipe,
  WlTimeZoneNamePipe,
  WlTimeZoneAbbrPipe,
  S3FilenamePipe,
  EllipsisTruncaterPipe,
  PrettifyHtmlPipe,

  // directives
  WlModalBodyScrollToTopDirective,
  WlNgModelsValidDirective,
  WlScrollToTopDirective,
  WlModalCssClassDirective,
  WlOnEnterPressDirective,
  InputGroupFocusRemoverDirective,
  AutoFocusDirective,
  CurrencySymbolComponent,
  UnauthenticatedComponent,

  TopNavComponent,
  LeftNavComponent,
  FooterComponent,
  NoAccessComponent,
  ListBuilderComponent,
  ExpressionComponent,
  ExpressionSummaryComponent,
  ExpressionOptionComponent,
  ExpressionOptionDropdownOptionComponent,
  WlRadioCheckboxComponent,
  PagingControlsComponent,
  NotificationComponent,
  DialogComponent,
  ConfirmSafeguardDialogComponent,
  CronBuilderComponent,
  NestedMenuComponent,
  ProfileLookupComponent,
  DateTimeSelectorComponent,
  WlRevenueItemMultiSelectorComponent,
  EventsDashboardComponent,
  EventDetailComponent,
  DashboardComponent,
  DashboardMembershipActivityComponent,
  DashboardRetentionComponent,
  DashboardEventsComponent,
  DashboardRevenueComponent,
  DashboardDuesAgingComponent,
  DashboardNonDuesAgingComponent,
  DashboardSalesOpportunitiesComponent,
  DashboardTasksComponent,
  DashboardNeoComponent,
  DashboardNeoConfigComponent,
  DashboardNeoMembershipActivityComponent,
  DashboardNeoRetentionComponent,
  DashboardNeoRetentionDonutComponent,
  DashboardNeoRetentionMonthlyComponent,
  DashboardNeoEventsComponent,
  DashboardNeoRevenueComponent,
  DashboardNeoMembershipReportsComponent,
  DashboardNeoFinancialReportsComponent,
  DashboardNeoReportsComponent,
  DashboardNeoReportsConfigComponent,
  DashboardNeoDuesAgingComponent,
  DashboardNeoNonDuesAgingComponent,
  DashboardNeoSalesOpportunitiesComponent,
  DashboardNeoTasksComponent,
  EventsSearchComponent,
  EventSettingsComponent,
  EventItemCreditTypeListComponent,
  EventSettingsRegistrationFieldsFieldRowComponent,
  EventSettingsPromoCodesComponent,
  EventSettingsSponsorsComponent,
  EventRegistrationDashboardComponent,
  EventNewRegistrationComponent,
  EventLeftNavComponent,
  EventsContextSearchComponent,
  EventDetailComponent,
  EventSettingsGeneralComponent,
  EventSettingsItemsComponent,
  EventSettingsPaymentMethodsComponent,
  EventSettingsRegistrationSettingsComponent,

  EventSettingsRegistrationFieldsComponent,
  EventSettingsRegistrationDropDownValuesComponent,
  RegistrationWizardComponent,
  RegistrationWizardContactComponent,
  RegistrationWizardAttendeeComponent,
  WLRegistrationWizardAttendeeTabNav,
  RegistrationWizardAttendeesComponent,
  RegistrationWizardItemsComponent,
  RegistrationWizardPaymentInfoComponent,
  RegistrationWizardSponsorComponent,
  RegistrationWizardModificationOptionsComponent,
  RegistrationWizardSponsorComponent,
  EventCopyComponent,
  RegistrationManagementComponent,
  RegistrationCustomFieldComponent,
  RegistrationCustomFieldsComponent,
  RegistrationDeleteComponent,
  RegistrationManagementSelectorComponent,
  EventsNewComponent,
  AttendeeManagementComponent,
  EventDashboardComponent,
  EventRegistrationFieldDropdownValueCreditTypeListComponent,
  WlTabsInModalDirective,
  WlModuleToolbarComponent,
  WlModuleEventsToolbarComponent,
  WlSelectEventComponent,
  WlEventDeleteModalComponent,
  WlModuleProfilesToolbarComponent,
  ToolbarModuleRevenueComponent,
  NewInvoiceDialogComponent,
  BatchPaymentDialogComponent,
  EventTypeEditComponent,
  ImportRegistationsComponent,
  ClassTypeEditComponent,
  ProfileBenefitMgtComponent,
  BenefitMgtComponent,

  BenefitMgtAdvancedSearchComponent,
  BenefitAddComponent,
  EventSettingsBenefitsComponent,
  BenefitFlagModalComponent,
  MassCommMessageAdminComponent,
  MassCommMessageAdminAnalyticsComponent,
  MassCommUndeliverableEmailGridComponent,
  MemberEmailEngagementGridComponent,
  MassCommMessageAdminDraftsComponent,
  MassCommMessageAdminRecurringComponent,
  MassCommMessageAdminScheduledMessagesComponent,
  MassCommMessageAdminSentMessagesComponent,
  MassCommMessageEditComponent,
  MassCommMessageEditMessageComponent,
  MassCommMessageEditProfilesComponent,
  MassCommMessageEditProfilesAddManualComponent,
  MassCommMessageEditProfilesEmailTypeConfigComponent,
  MassCommMessageEditSendComponent,
  MassCommMessageEditSendComponentAttachments,
  MassCommMessageEditAnalyticsComponent,
  MassCommMessageEditAnalyticsDeliveryDetailsComponent,
  MassCommMessageEditAnalyticsClickDetailsComponent,
  MassCommMessageEditAnalyticsResendDialogComponent,
  MassCommMessageHtmlEditorDialogComponent,
  MassCommClickedEmailLinksGridComponent,
  MassCommProfileSelectorDialogComponent,
  CardConnectTransComponent,
  CreditCardChargeComponent,
  InvoiceLookupComponent,
  InvoiceSelectorComponent,
  InvoiceSelectorFormComponent,
  CreditCardTransactionsComponent,
  InvoiceSelectorSummaryComponent,
  ProfileStatusComponent,
  ContactTypeSearchComponent,
  ProfileSearchSummaryComponent,
  ComonDataViewSelectorComponent,
  ProfileSearchComponent,
  ProfileSearchManageColumnsComponent,
  ProfileSearchModalComponent,
  ProfileSearchAdvancedOptionsComponent,
  MemberActivitySearchActionComponent,
  AssignUserSearchActionComponent,
  SetStatusSearchActionComponent,
  CreateContactSearchActionComponent,
  CreateTaskSearchActionComponent,
  AddReferralDialogComponent,
  ProfileFinderComponent,
  ProfileViewComponent,
  ProfileViewRevenueComponent,
  ProfileViewAffiliationComponent,
  ProfileViewCustomFieldComponent,
  ProfileViewMemberActivityComponent,
  ProfileViewPrimaryRelationsComponent,
  ProfileViewEventsComponent,
  ProfileViewRecentContactsComponent,
  ProfileViewReferralReportComponent,
  ProfileViewBenefitsComponent,
  ProfileViewCertificationsComponent,
  ProfileViewSalesOpportunitiesComponent,
  ProfileViewTasksComponent,
  ProfileViewVoterVoiceComponent,
  ProfileViewCommitteesComponent,
  ProfileAddToCommitteeComponent,
  ProfileAddAsMemberComponent,
  ProfileNotificationsComponent,
  ProfileViewWebContentComponent,
  ProfileEditComponent,
  ProfileEditGeneralComponent,
  ProfileEditMembershipComponent,
  ProfileEditAddressComponent,
  ProfileEditAddressListComponent,
  ProfileEditPhoneListComponent,
  ProfileEditEmailsComponent,
  ProfileEditSocialMediaComponent,
  ProfileEditAffiliationsComponent,
  ProfileEditWebPortalCredentialsComponent,
  ProfileEditListingListComponent,
  ProfileEditListingComponent,
  ProfileEditBillingComponent,
  ProfileEditBillingListComponent,
  ProfileEditRelationshipComponent,
  ProfileEditCustomFieldsComponent,
  ProfileEditProfileTypeComponent,
  ProfileEditDeleteComponent,
  ProfileEditNotesComponent,
  ProfileEditEmbeddedVideoComponent,
  NaaProfileTypeEditComponent,
  NahbProfileTypeEditComponent,
  ProfileTypeManagementCompanyEditComponent,
  ProfileTypeOwnerEditComponent,
  ProfileTypePropertyEditComponent,
  ProfileRelationshipsComponent,
  ProfileRelateExistingComponent,
  ProfileNewComponent,
  ProfileContactsComponent,
  ProfileContactCardComponent,
  ProfileContactEditComponent,
  ProfileContactsAnalyticsDialogComponent,
  ProfileReferralValueReportComponent,
  HttpRequestItemsComponent,
  AdminSettingsComponent,
  MailChimpSyncComponent,
  ContentManagementLink,
  FormBuilderComponent,
  FormBuilderEditElementComponent,
  FormBuilderProfileEditElementComponent,
  FormBuilderProfileDisplayElementComponent,
  FormBuilderSocialMediaEditElementComponent,
  FormBuilderSocialMediaDisplayElementComponent,
  FormBuilderListingEditElementComponent,
  FormBuilderListingDisplayElementComponent,
  FormBuilderProfileAddressEditElementComponent,
  FormBuilderProfileAddressDisplayElementComponent,
  FormBuilderHbaProfileDataDisplayElementComponent,
  FormBuilderHbaProfileDataEditElementComponent,
  FormBuilderSettingsComponent,
  FormBuilderMembershipLevelEditElementComponent,
  FormBuilderMembershipLevelDisplayElementComponent,
  FormsDashboardComponent,
  FormBuilderAffiliationChecklistEditElementComponent,
  FormBuilderAffiliationChecklistDisplayElementComponent,
  FormBuilderFileUploadEditElementComponent,
  FormBuilderFileUploadDisplayElementComponent,
  FormBuilderEmailOptIntEditElementComponent,
  FormBuilderEmailOptInDisplayElementComponent,
  ArcDashboardComponent,
  ArcBuilderComponent,
  InvoiceFinderComponent,
  InvoiceFinderWriteOffComponent,
  TransactionsGridComponent,
  UnappliedPaymentsGridComponent,
  ExpandableTransactionDescriptionComponent,
  TransactionsGridEmailDialogComponent,
  TransactionsGridApplyPaymentsDialogComponent,
  CreateUnappliedPaymentDialogComponent,
  ApplyUnappliedPaymentsDialogComponent,
  ProfileTransactionsComponent,
  ProfileMemberActivityEditingComponent,
  ProfileWebContentComponent,
  ProfileSalesOpportunitiesComponent,
  ProfileTasksSearchComponent,
  ProfileContactRecordsComponent,
  InvoiceBuilderComponent,
  InvoiceLineItemsListComponent,
  InvoiceLineItemComponent,
  InvoicePaymentsListComponent,
  InvoicePaymentComponent,
  InvoicePaymentHeaderComponent,
  InvoicePaymentEditComponent,
  InvoiceAdjustmentsListComponent,
  InvoiceAdjustmentComponent,
  InvoiceAdjustmentEditComponent,
  InvoiceSummaryComponent,
  InvoiceDetailsComponent,
  InvoiceDeleteComponent,
  CreditCardPaymentComponent,
  NewMemberInvoiceComponent,
  InvoiceEditComponent,
  InvoiceGeneratorComponent,
  BillingAmountUpdateComponent,
  MultiInvoicePaymentComponent,
  InvoiceModificationComponent,
  ReportFilesComponent,
  ReportStandardReportFilesComponent,
  ReportCustomReportFilesComponent,
  ReportSavedQueriesComponent,
  ReportQueryToolComponent,
  ReportsQueryToolDateParamDialogComponent,
  QueryToolAdvancedComponent,
  QueryToolSelectFieldsComponent,
  QueryToolSetFiltersComponent,
  QueryToolSetSortOrderComponent,
  QueryToolViewResultsComponent,
  ReportsQueryToolSavedQueriesComponent,
  ReportsRunAReportComponent,
  QueryToolViewSqlComponent,
  QueryToolSaveQueryModalComponent,
  DataDictionaryComponent,
  NAHBExportComponent,
  NahbExportValidationModalComponent,
  ManageWebContentComponent,
  WebContentSearchParamsComponent,
  WebContentSearchGridComponent,
  WebContentEditDialogComponent,
  WebContentEditComponent,
  CeuEditDialogComponent,
  CertificationEditDialogComponent,
  SalesOpportunityEditComponent,
  SalesOpportunityEditDialogComponent,
  SalesOpportunitySearchParamsComponent,
  SalesOpportunitySearchGridComponent,
  ContactRecordSearchParamsComponent,
  ContactRecordSearchGridComponent,
  DateSearchExpressionComponent,
  TaskSearchGridComponent,
  TaskSearchParamsComponent,
  TaskEditDialogComponent,
  TaskEditComponent,
  TaskGenerateDialogComponent,
  CommitteeEditComponent,
  CommitteeDetailsEditComponent,
  CommitteeMembersComponent,
  CommitteeMembersSearchParamsComponent,
  CommitteeMembersSearchGridComponent,
  CommitteeMeetingsComponent,
  CommitteeMeetingsSearchParamsComponent,
  CommitteeMeetingsSearchGridComponent,
  CommitteeMemberEditDialogComponent,
  CommitteeMemberEditComponent,
  CommitteeMemberProfileGridComponent,
  CommitteeMemberGroupAddDialogComponent,
  CommitteeSearchComponent,
  CommitteeSearchParamsComponent,
  CommitteeSearchGridComponent,
  CommitteeAddDialogComponent,
  CommitteeMeetingEditComponent,
  CommitteeMeetingDetailsEditComponent,
  CommitteeMeetingAgendaEditComponent,
  CommitteeMeetingMinutesEditComponent,
  CommitteeMeetingAttendanceEditComponent,
  CommitteeMeetingAttendanceGridComponent,
  CommitteeMeetingAttendanceGuestAddDialogComponent,
  CommitteeMemberSearchActionsComponent,
  BannerAdSearchComponent,
  BannerAdSearchParamsComponent,
  BannerAdSearchGridComponent,
  BannerAdsEmbedCodeDialogComponent,
  CouponSearchComponent,
  CouponSearchParamsComponent,
  CouponSearchGridComponent,
  CouponAddDialogComponent,
  NewsArticlesSearchComponent,
  NewsArticlesSearchParamsComponent,
  NewsArticlesSearchGridComponent,
  BannerAdSearchComponent,
  BannerAdSearchParamsComponent,
  BannerAdSearchGridComponent,
  BannerAdEditDialogComponent,
  BannerAdEditComponent,
  NewsArticleEditDialogComponent,
  QboAuthenticationLandingComponent,
  ProfileRelationshipGridComponent,
  SubmissionsComponent,
  SubmissionFormSettingsComponent,
  SubmissionListComponent,
  SubmissionReviewModalComponent,
  ProfileHistoryComponent,
  MultipleProfileInvoiceModalComponent,
  ProfileSelectorResultsComponent,
  PublicLinksComponent,
  PublicLinksUpgradeDialogComponent,
  PublicLinksProgressDialogComponent,
  UserPasswordChangeComponent,
  ProfileEditDropActivityDialogComponent,
  MassCommSubscriptionTrackingComponent
];

export function init_app(appLoadService: AppLoadService) {
  return () => appLoadService.initializeApp();
}

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

//export class TabList {}

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    AtlasSharedModule,
    ThirdPartySharedModule.forRoot(),
    RevenueModule,
    CommonModule,
    ConfirmDialogModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgxMaskModule.forRoot(),
    DragDropModule,
    FormBuilderModule,
    EcomModule,
    EventsV4Module,
  ],
  bootstrap: [AppComponent],
  providers: [
    // { provide: TabList, useValue: {} },
    atlasProviders,
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: '.dark-mode',
          primary: 'blue',
          cssLayer: { name: 'primeng', order: 'theme, base, primeng' },
        },
      },
    }),
  ],
  declarations: [AppComponent, MainContainerComponent, WorkspaceComponent, atlasDeclarations],
  exports: [
    WlModalBodyScrollToTopDirective,
    WlNgModelsValidDirective,
    WlScrollToTopDirective,
    WlModalCssClassDirective,
    WlOnEnterPressDirective,
    InputGroupFocusRemoverDirective,
  ],
})
export class AppModule {}
